import { RootState } from "@/store/types";
import { SmartClassifierState } from "./types";
import { ActionTree } from "vuex";
import { rest } from "@/store/api";

/**
 * Actions for the Smart Classifier module.
 * Handles async operations for ML-based intent classification.
 */
const actions: ActionTree<SmartClassifierState, RootState> = {
  /**
   * Fetches intents and fallback configuration from the API.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {string} brain - Brain/bot identifier
   * @returns {Promise<void>}
   */
  SMART_CLASSIFIER_FETCH_INTENTS: ({ commit }, brain) => {
    commit("SMART_CLASSIFIER_SET_FETCHING", true);
    return rest("post", "get_smart_classifier_intents", { brain })
      .then((response) => {
        commit("SMART_CLASSIFIER_SET_INTENTS", response.dataset);
        commit("SMART_CLASSIFIER_SET_FALLBACK", response.fallback);
        commit("SMART_CLASSIFIER_SET_FETCHING", false);
      })
      .catch(() => {
        const dataset = [];
        const fallback = "fallback_too_complex";
        commit("SMART_CLASSIFIER_SET_INTENTS", dataset);
        commit("SMART_CLASSIFIER_SET_FALLBACK", fallback);
        commit("SMART_CLASSIFIER_SET_FETCHING", false);
      });
  },

  /**
   * Saves intents to the API after filtering and deduplication.
   * Removes empty intents and duplicate intent names before saving.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {Object} payload - Action payload
   * @param {string} payload.brain - Brain/bot identifier
   * @returns {Promise<Object|boolean>} API response or false on failure
   */
  SMART_CLASSIFIER_SAVE_INTENT: async ({ state, commit }, { brain }) => {
    const intents = state.intents;
    // remove empty intent
    const filteredIntents = intents.filter((intent) => intent.intentName !== "");
    // remove duplicate intent
    const uniqueIntents = filteredIntents.filter(
      (intent, index) =>
        filteredIntents.findIndex((i) => i.intentName === intent.intentName) === index
    );

    const result = await rest("post", "save_smart_classifier_intent", {
      brain,
      intents: { dataset: uniqueIntents, fallback: state.fallback },
    });
    if (result) {
      commit("SMART_CLASSIFIER_SET_INTENTS", result.dataset);
      return result;
    }
    return false;
  },

  /**
   * Updates the fallback intent name in state.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {string} payload - New fallback intent name
   * @returns {void}
   */
  SET_SMART_CLASSIFIER_FALLBACK: ({ commit }, payload) => {
    commit("SMART_CLASSIFIER_SET_FALLBACK", payload);
  },

  /**
   * Deletes an intent by name from the store.
   *
   * @param {ActionContext} context - Vuex action context
   * @param {string} intentName - Name of intent to delete
   * @returns {Promise<void>}
   */
  SMART_CLASSIFIER_DELETE_INTENT: async ({ state, commit }, intentName) => {
    commit("SMART_CLASSIFIER_DELETE_INTENT", intentName);
  },
};

export default actions;
